import {Component, computed, OnInit, signal, Signal, TemplateRef} from '@angular/core';
import {environment} from '../../../environments/environment';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {catchError, distinctUntilChanged, filter, map, shareReplay, tap} from 'rxjs/operators';
import {BehaviorSubject, combineLatest, forkJoin, from, mergeMap, Observable, of, switchMap, toArray} from 'rxjs'
import {Apollo} from 'apollo-angular';

import {
  Airline,
  Mutation as WorkflowMutation,
  Po,
  PoItem,
  PoItemInput,
  ProcessStateEnum,
  Query as WorkflowQuery
} from '../../types-workflow';
import {CREATE_UPLOAD_TICKET} from '../../queries-repository';
import {PO_ITEM_LIST_FULL, PUBLISH_PO_ITEM} from '../../queries-workflow';
import {FinalizePoDialogComponent} from './finalize-po-dialog/finalize-po-dialog.component';
import {Mutation, Organization} from '../../types-repository';

import {BreadCrumbs, LogoAreaComponent} from '../logo-area/logo-area.component';
import {Dialog} from '@angular/cdk/dialog';
import {SelectionModel} from '@angular/cdk/collections';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ContextService} from '../../services/context.service';
import {IsAllowedPipe} from '../../pipes/has-action-pipe';
import {HeadlineSectionComponent} from '../headline-section/headline-section.component';
import {FilterSectionComponent} from '../filter-section/filter-section.component';
import {AsyncPipe, CommonModule} from '@angular/common';
import {PoItemLineComponent} from './po-item-line/po-item-line.component';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {ManageFilesDialogComponent} from './manage-files-dialog/manage-files-dialog.component';
import gql from 'graphql-tag';
import {ProcessService} from '../../services/process.service';
import {BannerService} from '../../banner/banner.service';
import {PoItemSelectionService} from '../../services/po-item-selection.service';
import {PO_DESTINATIONS} from '../../target_systems';
import {ConfirmDialogComponent} from '../../confirm-dialog/confirm-dialog.component';

interface Upload {
  url: string;
  text: string;
}

interface BatchOperationResult {
  success: boolean;
  poItemId: string;
  error?: any;
}


@Component({
  selector: 'app-list-po-items2',
  templateUrl: './list-po-items2.component.html',
  styleUrls: ['./list-po-items2.component.scss'],
  imports: [
    IsAllowedPipe,
    ReactiveFormsModule,
    HeadlineSectionComponent,
    LogoAreaComponent,
    FilterSectionComponent,
    RouterModule,
    AsyncPipe,
    PoItemLineComponent,
    CommonModule]
})
export class ListPoItems2Component implements OnInit {
  opened: SelectionModel<PoItem> = new SelectionModel<PoItem>(false, [], true, ((a, b) => a.po_item_id === b.po_item_id))

  airline: Signal<Airline>;
  organization: Signal<Organization>;


  filters: FormGroup = new FormGroup({
    t: new FormControl<string>(''),
    e: new FormControl<string>(null),
    p: new FormControl<string>(null),
  });


  menu = new BehaviorSubject<TemplateRef<any>>(null);
  uploads: Upload[] = [];

  po: Signal<Po>;
  poitems: Signal<PoItem[]>;
  poitems_filtered: Signal<PoItem[]>;
  hasMetadata: Signal<boolean>;
  vtkTemplates: Signal<string[]>;

  loading = signal(true);

  breadCrumbs: Signal<BreadCrumbs>;

  prefix$: Observable<string>

  // Selection state computed from service
  selectedItems: Signal<PoItem[]>;
  selectedCount: Signal<number>;
  canBatchPublish: Signal<boolean>;

  // Batch operation states
  batchPublishInflight = signal(false);
  batchRestartInflight = signal(false);

  // Finalize PO operation state
  finalizeInflight: Signal<boolean>;

  public readonly VTK_SERVER = environment.VTK;

  finalizeVtkData = computed((): { vtk_job_id: string; organization_urn: string } | null => {
    const data = this.po()?.finalize_process?.data;
    if (!data) return null;
    try {
      const parsed = JSON.parse(data);
      if (parsed?.vtk_job_id && parsed?.organization_urn) return parsed;
      return null;
    } catch {
      return null;
    }
  });


  constructor(private route: ActivatedRoute,
              private organizationService: ContextService,
              protected dialog: Dialog,
              private apollo: Apollo,
              private processService: ProcessService,
              private banner: BannerService,
              private selectionService: PoItemSelectionService) {
    this.organization = this.organizationService.organization;
    this.airline = this.organizationService.airline;
    this.prefix$ = route.queryParamMap.pipe(
      map(params => params.get('prefix')),
    )


    const po_id = this.route.params.pipe(
      map(params => params['po']),
      distinctUntilChanged(),
    ) as Observable<String>;
    const iata_code = this.route.params.pipe(
      map(params => params['airline']),
      distinctUntilChanged()
    ) as Observable<String>;

    const appSyncRes$ =
      combineLatest([po_id, toObservable(this.organization).pipe(filter(o => !!o))]).pipe(
        tap(() => {
          this.loading.set(true)
        }),
        switchMap(([po_name, org]) => {
          return this.apollo.use('workflow').watchQuery<WorkflowQuery>({
            query: PO_ITEM_LIST_FULL(org.id),
            nextFetchPolicy: 'cache-and-network',
            pollInterval: 10000,
            errorPolicy: 'ignore',
            variables: {
              'po_name': po_name
            }
          }).valueChanges
        }),
        filter(res => !!res.data),
        tap(() => {
          this.loading.set(false)
        }),
        shareReplay()
      )

    this.po = toSignal(appSyncRes$.pipe(
      map(wfq => wfq.data?.list_POs?.pos?.[0] as Po)
    ))

    this.poitems = toSignal(appSyncRes$.pipe(
      map(wfq => {
        const data = wfq.data
        if (!data?.list_POs?.pos[0]) {
          return [] as PoItem[]
        }
        const p = [...data.list_POs.pos[0].poitems] as PoItem[]
        p.sort((a, b) => {
          return a.po_item_id > b.po_item_id ? 1 : -1
        })
        return p
      }),
      tap(() => this.loading.set(false)),
    ))
    this.hasMetadata = computed(() => {
      return this.poitems().some(poItem => poItem.checkpoint_metadata_available != null)
    });

    this.vtkTemplates = computed(() => {
      const templates = new Set<string>();
      const items = this.poitems() ?? [];
      items.forEach(poItem => {
        if (poItem.vtk_template) {
          templates.add(poItem.vtk_template);
        }
      });
      return Array.from(templates).sort();
    });

    const filterChangedSignal = toSignal(this.filters.valueChanges)

    this.poitems_filtered = computed(() => {
      const filters = filterChangedSignal();
      const poitems = this.poitems();
      if (poitems && filters) {
        return poitems.filter(po => (
            po.po_item_id.toLowerCase().includes(filters.t.toLowerCase()) ||
            po.filename.toLowerCase().includes(filters.t.toLowerCase()))
          && (!filters.e || filters.e === '' || filters.e === po.workflow_process.state)
          && (
            filters.p === null ||
            (!po.publish_process && filters.p === 'NOT_STARTED') ||
            (po.publish_process && (!filters.p || filters.p === po.publish_process.state)))
        )
      } else {
        return poitems || [];
      }
    })

    // Initialize selection computed signals
    this.selectedItems = computed(() => {
      const po = this.po();
      const items = this.poitems();
      if (!po || !items) {
        return [];
      }

      // Access selections to track changes
      const selections = this.selectionService.selections();
      return this.selectionService.getSelectedItems(po.id, items);
    });

    this.selectedCount = computed(() => {
      return this.selectedItems().length;
    });

    this.canBatchPublish = computed(() => {
      const items = this.selectedItems();
      if (items.length === 0) {
        return false;
      }
      return items.every(item =>
        PO_DESTINATIONS[item.po_destination] != null &&
        PO_DESTINATIONS[item.po_destination].publishable
      );
    });

    this.finalizeInflight = computed(() => {
      const po = this.po();
      return po?.finalize_process?.state === 'IN_PROGRESS';
    });

  }


  genUploads() {
    const uploadObservables = this.poitems().map(poitem => {

          if (this.organizationService.isAllowed(
            'contentplatform:repository:Mutation/CreateUploadTicket',
            `contentplatform:folder:input.folder_id/s3://${poitem.input_brefix}`
          )()) {
            return this.apollo.mutate<Mutation>({
              mutation: CREATE_UPLOAD_TICKET,
              variables: {
                folder_id: `s3://${poitem.input_brefix}`,
                message: poitem.po_item_id
              },
              fetchPolicy: 'network-only'
            }).pipe(
              map(d => ({
                url: d.data.createUploadTicket.url,
                text: poitem.po_item_id
              }))
            );
          } else {
            return of({
              url: null,
              text: `You are not allowed to upload to ${poitem.input_brefix}`
            });
          }
        }
      )
    ;

    this.loading.set(true);
    forkJoin(uploadObservables).subscribe(u => {
      this.uploads = u;
      this.loading.set(false);
    });
  }

  ngOnInit() {
    this.breadCrumbs = computed(() => {
        const po_name = this.po()?.po_name;
        const airline = this.airline()?.iata_code;
        const org_id = this.organization()?.id;
        return [
          {
            display: 'Content Platform',
            commands: ['/o', org_id],
          },
          {
            display: 'Workflows',
            commands: ['/o', org_id, 'workflows'],
          },
          {
            display: this.airline()?.airline_name,
            commands: ['/o', org_id, 'workflows', airline],
          }
        ];
      }
    )
  }

  openDialog(templateRef: TemplateRef<any>) {
    this.dialog.open(templateRef);
  }

  publish() {
    this.dialog.open(FinalizePoDialogComponent, {
      data: {po_name: this.po().id}
    })
  }

  manageFiles() {
    const cleanPoName = this.po().id.replace(this.organization().id, this.organization().name);
    this.dialog.open(ManageFilesDialogComponent, {data: {header: `Manage Output: ${cleanPoName}`, po_name: cleanPoName}})
  }

  checked() {
    const numSelected = this.selectedCount();
    const numRows = this.poitems_filtered()?.length || 0;
    return numSelected > 0 && numSelected === numRows;
  }

  indeterminate() {
    const numSelected = this.selectedCount();
    const numRows = this.poitems_filtered()?.length || 0;
    return numSelected > 0 && numSelected < numRows;
  }

  toggleAll() {
    const po = this.po();
    if (!po) {
      return;
    }

    const allSelected = this.checked();
    if (allSelected) {
      this.selectionService.clear(po.id);
    } else {
      const itemIds = this.poitems_filtered().map(item => item.po_item_id);
      this.selectionService.selectAll(po.id, itemIds);
    }
  }

  toggleSelection(poItem: PoItem) {
    const po = this.po();
    if (!po) {
      return;
    }
    this.selectionService.toggle(po.id, poItem.po_item_id);
  }

  isSelected(poItem: PoItem): boolean {
    const po = this.po();
    if (!po) {
      return false;
    }
    return this.selectionService.isSelected(po.id, poItem.po_item_id);
  }

  clearSelection() {
    if (this.batchPublishInflight() || this.batchRestartInflight()) {
      return;
    }
    const po = this.po();
    if (!po) {
      return;
    }
    this.selectionService.clear(po.id);
  }

  batchPublish() {
    const selectedItems = this.selectedItems();
    if (selectedItems.length === 0 || this.batchPublishInflight()) {
      return;
    }

    // Check if any selected items are already published or in progress
    const alreadyPublished = selectedItems.filter(item =>
      item.publish_process?.state === ProcessStateEnum.Success ||
      item.publish_process?.state === ProcessStateEnum.InProgress
    );

    const performBatchPublish = () => {
      this.batchPublishInflight.set(true);

      // Fire mutations with concurrency limit to avoid overwhelming the backend.
      from(selectedItems).pipe(
        mergeMap(poItem =>
          this.apollo.use('workflow').mutate<WorkflowMutation>({
            mutation: PUBLISH_PO_ITEM,
            variables: {
              po_name: poItem.po.po_name,
              po_item_id: poItem.po_item_id,
            }
          }).pipe(
            tap((res) => {
              this.selectionService.deselect(poItem.po.po_name, poItem.po_item_id);
              const client = this.apollo.use('workflow').client;
              const cache = client.cache;
              cache.modify({
                id: cache.identify({__typename: 'POItem', id: poItem.id}),
                fields: {
                  publish_process(existingPublishProcess, {toReference}) {
                    return toReference(res.data.publishPoItem);
                  },
                },
              });
            }),
            map(() => ({ success: true, poItemId: poItem.po_item_id } as BatchOperationResult)),
            catchError((error) => {
              console.error(`Failed to publish ${poItem.po_item_id}:`, error);
              return of({ success: false, poItemId: poItem.po_item_id, error } as BatchOperationResult);
            })
          ),
          5 // concurrency limit
        ),
        toArray()
      ).subscribe({
        next: (results) => {
          const successCount = results.filter(r => r.success).length;
          const failCount = results.filter(r => !r.success).length;
          this.batchPublishInflight.set(false);

          if (failCount > 0) {
            this.banner.open(`Started publishing ${successCount} item(s). ${failCount} failed to start.`, ['Close']);
          }
        },
        error: () => {
          this.batchPublishInflight.set(false);
          this.banner.open('Batch publish operation failed.', ['Close']);
        }
      });
    };

    if (alreadyPublished.length > 0) {
      const successCount = alreadyPublished.filter(item => item.publish_process?.state === ProcessStateEnum.Success).length;
      const inProgressCount = alreadyPublished.filter(item => item.publish_process?.state === ProcessStateEnum.InProgress).length;

      let message = `You selected ${selectedItems.length} item(s) for publishing.<br/>`;
      if (successCount > 0) {
        message += `${successCount} item(s) have already been published.<br/>`;
      }
      if (inProgressCount > 0) {
        message += `${inProgressCount} item(s) are currently being published.<br/>`;
      }
      message += 'Do you want to republish all selected items?';

      this.dialog.open(ConfirmDialogComponent, {
        data: {
          headline: 'Publish Selected Items',
          message: message,
          actionName: 'Publish All',
          action: performBatchPublish
        }
      });
    } else {
      performBatchPublish();
    }
  }

  batchRestart() {
    const selectedItems = this.selectedItems();
    if (selectedItems.length === 0 || this.batchRestartInflight()) {
      return;
    }

    this.batchRestartInflight.set(true);

    // Fire mutations with concurrency limit to avoid overwhelming the backend.
    // Each mutation's update callback uses cache.modify to update the UI as it completes.
    from(selectedItems).pipe(
      mergeMap(poItem =>
        this.apollo.use('workflow').mutate<WorkflowMutation>({
          mutation: gql`mutation restart_workflow($po_name: String!, $po_item_id: String!) {
            restartPoItem(input: { po_name: $po_name, po_item_id: $po_item_id }) {
              __typename
              id
              state
              data
              message
              start_date
              end_date
            }
          }`,
          variables: {
            po_name: poItem.po.po_name,
            po_item_id: poItem.po_item_id
          } satisfies PoItemInput,
          update: (cache, {data}) => {
            if (!data?.restartPoItem) return;
            this.selectionService.deselect(poItem.po.po_name, poItem.po_item_id);
            cache.modify({
              id: cache.identify({__typename: 'POItem', id: poItem.id}),
              fields: {
                tracks() { return []; },
                checkpoint_content_uploaded() { return false; },
                checkpoint_content_complete() { return false; },
                checkpoint_encodes_done() { return false; },
                vtk_jobs() { return []; },
                preview() { return null; },
                publish_process() { return null; },
                workflow_process() {
                  return {
                    __typename: 'WorkflowProcessPayload',
                    id: data.restartPoItem.id,
                    state: data.restartPoItem.state,
                    message: data.restartPoItem.message,
                    data: data.restartPoItem.data ?? null,
                    start_date: data.restartPoItem.start_date || new Date().toISOString(),
                    end_date: data.restartPoItem.end_date ?? null,
                  };
                },
              },
            });
          }
        }).pipe(
          map(() => ({ success: true, poItemId: poItem.po_item_id } as BatchOperationResult)),
          catchError((error) => {
            console.error(`Failed to restart ${poItem.po_item_id}:`, error);
            return of({ success: false, poItemId: poItem.po_item_id, error } as BatchOperationResult);
          })
        ),
        5 // concurrency limit
      ),
      toArray()
    ).subscribe({
      next: (results) => {
        const successCount = results.filter(r => r.success).length;
        const failCount = results.filter(r => !r.success).length;
        this.batchRestartInflight.set(false);

        if (failCount > 0) {
          this.banner.open(`Started encoding ${successCount} item(s). ${failCount} failed to start.`, ['Close']);
        }
      },
      error: () => {
        this.batchRestartInflight.set(false);
        this.banner.open('Batch restart operation failed.', ['Close']);
      }
    });
  }
}
